import type Application from "../../types/application";
import { Body, SecondaryBody } from "@sprout/typography";
import PreviewIcon from "@sprout/icons/preview";
import EllipsisIcon from "@sprout/icons/ellipsis";
import { Row, Stack } from "@sprout/layout";
import useTranslation from "@hooks/use_translation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu.tsx";
import { Dialog, DialogTrigger } from "@components/ui/dialog";
import DreamJobModal from "./dream_job_modal";
import { daysUntilNextMonth } from "@utils/date_utils";
import { useState } from "react";

type Props = {
  application: Application;
  hasTokens: boolean;
  onAddDreamJob: () => void;
  openInactiveModal: () => void;
};

const ApplicationEllipsis = ({
  application,
  hasTokens,
  onAddDreamJob,
  openInactiveModal,
}: Props) => {
  const [dreamJobDropdownOpen, setDreamJobDropdownOpen] = useState(false);
  const { t } = useTranslation("applications");

  const dreamJobDescription = () => {
    if (application.dream_job) {
      return t("dream_job.already_set");
    } else if (application.duplicate) {
      return t("dream_job.duplicate");
    } else if (application.inactive) {
      return t("dream_job.inactive");
    } else if (!hasTokens) {
      return t("dream_job.no_tokens", { count: daysUntilNextMonth() });
    } else {
      return t("dream_job.description");
    }
  };

  return (
    <Dialog>
      <DropdownMenu onOpenChange={setDreamJobDropdownOpen}>
        <DropdownMenuTrigger>
          <div
            className={`hover:bg-[#d6e6ff] rounded-full ${dreamJobDropdownOpen && "bg-[#eff1f0]"}`}
          >
            <EllipsisIcon />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-74" align="end">
          <DropdownMenuItem
            onSelect={() => {
              window.open(application.job_post_url, "_blank");
            }}
          >
            <Row align="center" gap={"12px"}>
              <Body>{t("dream_job.open_job_post")}</Body>
              <PreviewIcon />
            </Row>
          </DropdownMenuItem>
          <DialogTrigger
            disabled={
              !hasTokens || application.dream_job || application.inactive || application.duplicate
            }
          >
            <DropdownMenuItem>
              <Row gap={"8px"}>
                <Stack>
                  <Body>{t("dream_job.set")}</Body>
                  <div className="dream-job-subtext">
                    <SecondaryBody>{dreamJobDescription()}</SecondaryBody>
                  </div>
                </Stack>
              </Row>
            </DropdownMenuItem>
          </DialogTrigger>
          {!application.inactive && (
            <DropdownMenuItem onSelect={openInactiveModal}>
              <Body>{t("mark_as_inactive")}</Body>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <DreamJobModal application={application} afterSuccess={onAddDreamJob} />
    </Dialog>
  );
};

export default ApplicationEllipsis;
